'use client'

import React, { FC, useCallback, useMemo, useState, useRef } from 'react'
import Invoice from '@/components/search-input/components/invoice'
import Wallet from '@/utils/wallet'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import {
	NETWORK,
	MARKET_FEE_PERCENTAGE,
	MARKET_FEE_RATE,
	MARKET_ROYALTY_ADDRESS,
	TOOL_FEE_ADDRESS
} from '@/utils/constants'
import numberWithCommas from '@/utils/format-number'
import event from '@/utils/event'
import type { BitmapListing } from '@/utils/hooks/useBitmap'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'
import { isSnipeProtectedListing, protocolFromInscription, resolveBitcoinOutpoint, secureErrorMessage } from '@/utils/market/secure-purchase'
import { buildSecurePurchase, secureInvoice, type SecurePurchaseQuote } from '@/utils/market/secure-ticket-client'
import { snipeProtectionInvoiceItem } from '@/components/snipe-protection-toggle'

interface SelectedBitmapInfo {
	blockHeight: number
	listing: BitmapListing
}

interface BitmapInvoiceProps {
	selected: SelectedBitmapInfo[]
	onComplete: () => void
	onClose: () => void
}

const BitmapInvoice: FC<BitmapInvoiceProps> = ({ selected, onComplete, onClose }) => {
	const [builtPsbt, setBuiltPsbt] = useState<string | null>(null)
	const builtSetupRef = useRef<string | null>(null)
	const secureQuoteRef = useRef<SecurePurchaseQuote | null>(null)
	const [builtFee, setBuiltFee] = useState<number>(0)
	const [builtRebarFee, setBuiltRebarFee] = useState<number>(0)
	const [builtRebar, setBuiltRebar] = useState(false)
	const [invoiceError, setInvoiceError] = useState<string | null>(null)
	const [isRebuilding, setIsRebuilding] = useState(false)

	const { exchangeRate } = useExchangeRate()

	const totalPrice = useMemo(() => {
		return selected.reduce((total, item) => {
			return total + (item.listing?.satoshiPrice || 0)
		}, 0)
	}, [selected])

	const totalPriceBTC = totalPrice / 1e8
	const marketFeeBTC = totalPriceBTC * MARKET_FEE_RATE
	const marketFeeUSD = marketFeeBTC * exchangeRate
	const subtotalBTC = totalPriceBTC - marketFeeBTC
	const subtotalUSD = subtotalBTC * exchangeRate

	const invoiceBreakdown = useMemo(() => {
		if (!builtPsbt) return null
		// Protected buys: the OW fee is paid in T2, not in the T1 PSBT we hold,
		// so use the API's totals rather than scanning outputs.
		const secure = secureQuoteRef.current
		if (secure) {
			try {
				const parsed = secureInvoice(secure)
				return {
					marketFeeSat: parsed.marketFeeSat,
					toolFeeSat: 0,
					sellerProceedsSat: parsed.sellerProceedsSat,
					creatorRoyaltySat: parsed.creatorRoyaltySat,
					itemTotalSat: parsed.itemTotalSat,
					networkFeeSat: parsed.networkFeeSat,
					rebarFeeSat: 0,
					totalSat: parsed.totalSat
				}
			} catch (e) {
				console.error('[bitmap-invoice] secure invoice parse failed', e)
				return null
			}
		}
		try {
			const marketFeeSat = Wallet.getOutputAmountToAddress(builtPsbt, MARKET_ROYALTY_ADDRESS)
			const toolFeeSat = Wallet.getOutputAmountToAddress(builtPsbt, TOOL_FEE_ADDRESS)
			const uniqueSellerAddresses = Array.from(
				new Set(
					selected.flatMap((item) =>
						item.listing?.sellerAddress ? [item.listing.sellerAddress] : []
					)
				)
			)
			const sellerProceedsSat = uniqueSellerAddresses
				.map((addr) => Wallet.getOutputAmountToAddress(builtPsbt, addr as string))
				.reduce((a, v) => a + (v || 0), 0)
			const itemTotalSat = sellerProceedsSat + marketFeeSat + toolFeeSat
			const totalSat = itemTotalSat + builtFee + builtRebarFee
			return {
				marketFeeSat,
				toolFeeSat,
				sellerProceedsSat,
				itemTotalSat,
				networkFeeSat: builtFee,
				rebarFeeSat: builtRebarFee,
				totalSat
			}
		} catch (_e) {
			return null
		}
	}, [builtPsbt, builtFee, builtRebarFee, selected])

	const buildInvoice = useCallback(async (deps: any[], feeRate: number) => {
		const selectedItems = deps[0] as SelectedBitmapInfo[]
		if (feeRate && selectedItems.length > 0) {
			setIsRebuilding(true)
			setInvoiceError(null)
			try {
				const inscriptionIds = selectedItems.map((item) => item.listing.inscriptionId)
				// Settlement path follows the listings, not the deployment.
				const secureSelected = selectedItems.some((item) => isSnipeProtectedListing(item.listing))
				// A quote belongs to the build that produced it.
				secureQuoteRef.current = null
				if (secureSelected) {
					const outpoints = await Promise.all(
						inscriptionIds.map((id) => resolveBitcoinOutpoint(id))
					)
					const quote = await buildSecurePurchase({
						outpoints,
						protocol: protocolFromInscription({}),
						feeRate,
						listings: selectedItems.map((item) => ({
							sellerAddress: String(item.listing?.sellerAddress || ''),
							satoshiPrice: Number(item.listing?.satoshiPrice) || undefined
						}))
					})
					secureQuoteRef.current = quote
					setBuiltPsbt(quote.purchases[0]?.psbt || quote.ticketHex)
					builtSetupRef.current = null
					setBuiltFee(quote.totals.miner_fee_sats)
					setBuiltRebarFee(0)
					setBuiltRebar(false)
					return { psbt: quote.purchases[0]?.psbt || quote.ticketHex, fee: BigInt(quote.totals.miner_fee_sats) }
				}
				const data = await Wallet.getPurchases(inscriptionIds, feeRate)
				const totalFee = Number(data.setup_fee) + Number(data.purchase_fee)
				setBuiltPsbt(data.purchase)
				builtSetupRef.current = data.setup
				setBuiltFee(totalFee)
				setBuiltRebarFee(data.rebar_fee)
				setBuiltRebar(data.rebar)
				return { psbt: data.purchase, setup: data.setup, fee: BigInt(totalFee) }
			} catch (e: any) {
				console.error('Error building invoice:', e)
				setInvoiceError(
					secureQuoteRef.current
						? secureErrorMessage(e)
						: e?.response?.data?.message || 'An error occurred while building the invoice'
				)
				return { psbt: '', fee: BigInt(0) }
			} finally {
				setIsRebuilding(false)
			}
		}
		return { psbt: '', fee: BigInt(0) }
	}, [])

	const handleSubmit = useCallback(
		async (psbt: string, setup?: string) => {
			try {
				if (secureQuoteRef.current) {
					const quote = secureQuoteRef.current
					if (!quote) return
					const inscriptions = selected.map((item) => ({
						id: item.listing.inscriptionId,
						escrow: {
							satoshi_price: item.listing.satoshiPrice,
							seller_address: item.listing.sellerAddress
						},
						name: item.listing.name
					}))
					event.emit('open-purchase-modal', {
						inscriptions,
						purchaseData: {
							secureQuote: quote,
							// Parsed from the PSBTs (invoiceBreakdown); API totals only as fallback.
							networkFee: invoiceBreakdown?.networkFeeSat ?? quote.totals.miner_fee_sats,
							marketFee: invoiceBreakdown?.marketFeeSat ?? quote.totals.ow_fee_sats,
							totalPrice: invoiceBreakdown?.totalSat ?? quote.totals.total_sats,
							subtotalPrice: invoiceBreakdown?.sellerProceedsSat ?? quote.totals.seller_sats,
							creatorRoyalty: invoiceBreakdown?.creatorRoyaltySat ?? quote.totals.creator_royalty_sats
						}
					})
					return
				}
				const res = await Wallet.signPurchase(setup as any, psbt)
				const { signedSetup, signedPurchase, success } = res

				if (success) {
					const setupFee = signedSetup ? Wallet.getPSBTFee(signedSetup) : BigInt(0)
					const purchaseFee = builtRebar ? 0 : Number(Wallet.getPSBTFee(signedPurchase as any))
					const totalFee = Number(purchaseFee) + Number(setupFee)

					if (!invoiceBreakdown) {
						return
					}

					const marketFeeSat = invoiceBreakdown.marketFeeSat
					const sellerProceedsSat = invoiceBreakdown.sellerProceedsSat
					const itemTotalSat = invoiceBreakdown.itemTotalSat
					const totalPriceSat = itemTotalSat + totalFee + builtRebarFee

					// Convert selected items to inscription format for the purchase modal
					const inscriptions = selected.map((item) => ({
						id: item.listing.inscriptionId,
						escrow: {
							satoshi_price: item.listing.satoshiPrice,
							seller_address: item.listing.sellerAddress
						},
						name: item.listing.name
					}))

					event.emit('open-purchase-modal', {
						inscriptions,
						purchaseData: {
							setup: signedSetup,
							purchase: signedPurchase,
							networkFee: totalFee,
							marketFee: marketFeeSat,
							totalPrice: totalPriceSat,
							subtotalPrice: sellerProceedsSat,
							slug: 'bitmap',
							rebar: builtRebar,
							rebarFee: builtRebarFee
						}
					})

					onComplete()
				}
			} catch (e) {
				console.error('Error signing purchase:', e)
				throw e
			}
		},
		[selected, invoiceBreakdown, onComplete, builtRebar, builtRebarFee]
	)

	const invoiceItems = useMemo(
		() => [
			{
				title: 'Total',
				valueFunc: ({ building, fee }: { building: boolean; fee: number }) => {
					if (building) return '--'
					if (invoiceBreakdown) {
						const totalBTC = invoiceBreakdown.totalSat / 1e8
						const totalUSD = totalBTC * exchangeRate
						return `${totalBTC.toFixed(8)} ${NETWORK} / $${numberWithCommas(totalUSD.toFixed(2))}`
					}
					const networkFeeBTC = Number(fee) / 1e8
					const rebarFeeBTC = builtRebarFee / 1e8
					const totalWithFeeBTC = totalPriceBTC + networkFeeBTC + rebarFeeBTC
					const totalWithFeeUSD = totalWithFeeBTC * exchangeRate
					return `${totalWithFeeBTC.toFixed(8)} ${NETWORK} / $${numberWithCommas(
						totalWithFeeUSD.toFixed(2)
					)}`
				}
			},
			{
				title: 'Subtotal',
				valueFunc: () => {
					if (invoiceBreakdown) {
						const btc = invoiceBreakdown.sellerProceedsSat / 1e8
						const usd = btc * exchangeRate
						return `${btc.toFixed(8)} ${NETWORK} / $${numberWithCommas(usd.toFixed(2))}`
					}
					return `${subtotalBTC.toFixed(8)} ${NETWORK} / $${numberWithCommas(
						subtotalUSD.toFixed(2)
					)}`
				}
			},
			{
				title: `Market Fee (${MARKET_FEE_PERCENTAGE}%)`,
				valueFunc: () => {
					if (invoiceBreakdown) {
						const btc = invoiceBreakdown.marketFeeSat / 1e8
						const usd = btc * exchangeRate
						return `${btc.toFixed(8)} ${NETWORK} / $${numberWithCommas(usd.toFixed(2))}`
					}
					return `${marketFeeBTC.toFixed(8)} ${NETWORK} / $${numberWithCommas(
						marketFeeUSD.toFixed(2)
					)}`
				}
			},
			{
				title: 'Network Fee'
			},
			...snipeProtectionInvoiceItem(!!secureQuoteRef.current),
			...(builtRebar && builtRebarFee
				? [
						{
							title: 'Mempool Protection',
							valueFunc: () => {
								const btc = builtRebarFee / 1e8
								const usd = btc * exchangeRate
								return `${btc.toFixed(8)} ${NETWORK} / $${numberWithCommas(usd.toFixed(2))}`
							}
						}
				  ]
				: [])
		],
		[
			invoiceBreakdown,
			exchangeRate,
			totalPriceBTC,
			subtotalBTC,
			subtotalUSD,
			marketFeeBTC,
			marketFeeUSD,
			builtRebar,
			builtRebarFee
		]
	)

	const memoizedBuildDeps = useMemo(() => [selected], [selected])

	if (selected.length === 0) {
		return null
	}

	return (
		<div className="fixed inset-0 z-50 pointer-events-none">
			{/* Backdrop - click to close */}
			<div
				className="absolute inset-0 pointer-events-auto"
				onClick={onClose}
				role="button"
				tabIndex={0}
				onKeyDown={onKeyboardClick(onClose)}
			/>
			{/* Invoice - sits on top of footer, full width on mobile, right-aligned on desktop */}
			<div className="absolute left-0 right-0 md:left-auto md:right-0 bottom-[88px] md:w-[380px] pointer-events-auto border-t border-border md:border md:rounded-t-lg md:mr-4 shadow-2xl overflow-hidden">
				<Invoice
					open={true}
					onSubmit={handleSubmit}
					buildDeps={memoizedBuildDeps}
					onBuild={buildInvoice}
					action={`Buy ${selected.length} Inscription${selected.length !== 1 ? 's' : ''}`}
					items={invoiceItems}
					errorMessage={isRebuilding ? null : (invoiceError as any)}
					debounce={1500}
					fixed={false}
				/>
			</div>
		</div>
	)
}

export default BitmapInvoice
